import {
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";

type Row = Record<string, unknown>;
type TabCode = "health" | "runs" | "failures" | "outbox";
type ExportFormat = "CSV" | "XLS" | "JSON";

type ActionResult = {
  readonly success: boolean;
  readonly title: string;
  readonly message: string;
};

export type CmtsSystemOperationsPageProps = {
  readonly health?: unknown;
  readonly runs?: unknown;
  readonly failures?: unknown;
  readonly outbox?: unknown;
  readonly diagnostics?:
  | readonly ComponentDiagnostic[]
  | undefined;
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onRefresh?: (() => void | Promise<void>) | undefined;
  readonly onRetryOperation?:
  | ((row: Row) => void | Promise<void>)
  | undefined;
  readonly onCancelOperation?:
  | ((row: Row) => void | Promise<void>)
  | undefined;
  readonly onReplayOutbox?:
  | ((row: Row) => void | Promise<void>)
  | undefined;
  readonly onMarkFailureReviewed?:
  | ((row: Row) => void | Promise<void>)
  | undefined;
  readonly onCreateTask?:
  | ((row: Row) => void | Promise<void>)
  | undefined;
};

const healthColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "code",
    header: "Signal Code",
    accessor: readCode,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
  {
    id: "module",
    header: "Module",
    accessor: readModule,
    sortable: true,
    filterable: true,
    minWidth: 150,
  },
  {
    id: "signalType",
    header: "Signal",
    accessor: readSignalType,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    id: "summary",
    header: "Summary",
    accessor: readSummary,
    sortable: true,
    filterable: true,
    minWidth: 320,
  },
  {
    id: "updatedAt",
    header: "Updated",
    accessor: readUpdatedAt,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
];

const runColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "startedAt",
    header: "Started",
    accessor: readStartedAt,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
  {
    id: "operationType",
    header: "Operation Type",
    accessor: readOperationType,
    sortable: true,
    filterable: true,
    minWidth: 210,
  },
  {
    id: "module",
    header: "Module",
    accessor: readModule,
    sortable: true,
    filterable: true,
    minWidth: 150,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    id: "result",
    header: "Result",
    accessor: readResult,
    sortable: true,
    filterable: true,
    minWidth: 160,
  },
  {
    id: "failureReason",
    header: "Failure Reason",
    accessor: readFailureReason,
    sortable: true,
    filterable: true,
    minWidth: 300,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

const failureColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "operationType",
    header: "Operation",
    accessor: readOperationType,
    sortable: true,
    filterable: true,
    minWidth: 220,
  },
  {
    id: "severity",
    header: "Severity",
    accessor: readSeverity,
    sortable: true,
    filterable: true,
    minWidth: 145,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 155,
  },
  {
    id: "failureReason",
    header: "Reason",
    accessor: readFailureReason,
    sortable: true,
    filterable: true,
    minWidth: 320,
  },
  {
    id: "retryable",
    header: "Retryable",
    accessor: readRetryable,
    sortable: true,
    filterable: true,
    minWidth: 140,
  },
  {
    id: "occurredAt",
    header: "Occurred",
    accessor: readUpdatedAt,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

const outboxColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "sequence",
    header: "Sequence",
    accessor: readSequence,
    sortable: true,
    filterable: true,
    minWidth: 120,
  },
  {
    id: "entityType",
    header: "Entity",
    accessor: readEntity,
    sortable: true,
    filterable: true,
    minWidth: 170,
  },
  {
    id: "operation",
    header: "Operation",
    accessor: readOperationType,
    sortable: true,
    filterable: true,
    minWidth: 190,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 155,
  },
  {
    id: "replayStatus",
    header: "Replay",
    accessor: readReplayStatus,
    sortable: true,
    filterable: true,
    minWidth: 155,
  },
  {
    id: "failureReason",
    header: "Failure",
    accessor: readFailureReason,
    sortable: true,
    filterable: true,
    minWidth: 290,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

export function CmtsSystemOperationsPage(
  props: CmtsSystemOperationsPageProps,
) {
  const [activeTab, setActiveTab] =
    useState<TabCode>("health");
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [actionRow, setActionRow] = useState<Row | null>(
    null,
  );
  const [detailRow, setDetailRow] = useState<Row | null>(
    null,
  );
  const [correlationRow, setCorrelationRow] =
    useState<Row | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<ExportFormat>("CSV");
  const [busyAction, setBusyAction] = useState<
    string | null
  >(null);
  const [actionResult, setActionResult] =
    useState<ActionResult | null>(null);

  const health = useMemo(
    () => normalizeRows(props.health, "health"),
    [props.health],
  );
  const runs = useMemo(
    () => normalizeRows(props.runs, "run"),
    [props.runs],
  );
  const failures = useMemo(
    () => normalizeRows(props.failures, "failure"),
    [props.failures],
  );
  const outbox = useMemo(
    () => normalizeRows(props.outbox, "outbox"),
    [props.outbox],
  );

  const activeRows =
    activeTab === "health"
      ? health
      : activeTab === "runs"
        ? runs
        : activeTab === "failures"
          ? failures
          : outbox;

  const activeColumns =
    activeTab === "health"
      ? healthColumns
      : activeTab === "runs"
        ? runColumns
        : activeTab === "failures"
          ? failureColumns
          : outboxColumns;

  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return activeRows.filter((row) => {
      const text = [
        readCode(row),
        readModule(row),
        readOperationType(row),
        readSignalType(row),
        readEntity(row),
        readRawStatus(row),
        readFailureReason(row),
        readSummary(row),
      ]
        .join(" ")
        .toLowerCase();

      if (
        normalizedSearch &&
        !text.includes(normalizedSearch)
      ) {
        return false;
      }

      if (
        moduleFilter !== "ALL" &&
        readModule(row) !== moduleFilter
      ) {
        return false;
      }

      if (
        statusFilter !== "ALL" &&
        readRawStatus(row) !== statusFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    activeRows,
    search,
    moduleFilter,
    statusFilter,
  ]);

  const modules = uniqueValues(activeRows.map(readModule));
  const statuses = uniqueValues(
    activeRows.map(readRawStatus),
  );

  const healthyCount = health.filter((row) =>
    ["HEALTHY", "READY", "UP", "AVAILABLE"].includes(
      readRawStatus(row),
    ),
  ).length;

  const runningCount = runs.filter((row) =>
    ["RUNNING", "PENDING", "QUEUED", "PROCESSING"].includes(
      readRawStatus(row),
    ),
  ).length;

  const failedCount = failures.filter(
    (row) =>
      !["REVIEWED", "RESOLVED", "CLOSED"].includes(
        readRawStatus(row),
      ),
  ).length;

  const pendingOutboxCount = outbox.filter(
    (row) =>
      !["COMPLETED", "REPLAYED", "PROCESSED"].includes(
        readRawReplayStatus(row),
      ),
  ).length;

  const correlationRows = useMemo(() => {
    const correlationId = readRawCorrelation(correlationRow);

    if (!correlationId) {
      return [];
    }

    return [...runs, ...failures, ...outbox].filter(
      (row) => readRawCorrelation(row) === correlationId,
    );
  }, [correlationRow, runs, failures, outbox]);

  const gridActions: readonly CmtsGridRowAction<Row>[] = [
    {
      id: "system-actions",
      label: "⋮ Actions",
      onClick: setActionRow,
    },
  ];

  function changeTab(tab: TabCode) {
    setActiveTab(tab);
    setSearch("");
    setModuleFilter("ALL");
    setStatusFilter("ALL");
    setActionRow(null);
  }

  async function executeAction(
    actionName: string,
    row: Row,
    handler: ((row: Row) => void | Promise<void>) | undefined,
    successTitle: string,
    successMessage: string,
  ) {
    if (!handler) {
      return;
    }

    setActionRow(null);
    setBusyAction(actionName);

    try {
      await handler(row);

      setActionResult({
        success: true,
        title: successTitle,
        message: successMessage,
      });
    } catch (error) {
      setActionResult({
        success: false,
        title: `${successTitle} failed`,
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="cmts-system-blue">
      <PageContainer
        title="System Operations"
        subtitle="System health, operation runs, failure center, offline outbox and remediation controls."
      >
        {/* {props.diagnostics?.length ? (
          <RuntimeDiagnosticBanner
            diagnostics={props.diagnostics}
            title="System operations diagnostics"
          />
        ) : null} */}

        <section className="cmts-system-hero">
          <div>
            <span className="cmts-system-eyebrow">
              SYSTEM OPERATIONS
            </span>

            <h2>Operational control center</h2>

            <p>
              Monitor system health, operation runs, failures and
              offline replay activity from one governed operational
              workspace.
            </p>
          </div>

          <div className="cmts-system-hero__actions">
            <button
              type="button"
              className="cmts-system-secondary-button"
              disabled={!props.onRefresh || props.loading}
              onClick={() => void props.onRefresh?.()}
            >
              ↻ Refresh data
            </button>

            <button
              type="button"
              className="cmts-system-primary-button"
              disabled={visibleRows.length === 0}
              onClick={() => setExportOpen(true)}
            >
              ⇩ Export current view
            </button>
          </div>
        </section>

        <section className="cmts-system-summary-grid">
          <SummaryCard
            label="Health signals"
            value={health.length}
            description={`${healthyCount} currently healthy`}
            tone="blue"
            icon="♥"
          />

          <SummaryCard
            label="Operation runs"
            value={runs.length}
            description={`${runningCount} currently active`}
            tone="purple"
            icon="▶"
          />

          <SummaryCard
            label="Open failures"
            value={failedCount}
            description="Failures requiring attention"
            tone="red"
            icon="!"
          />

          <SummaryCard
            label="Pending outbox"
            value={pendingOutboxCount}
            description="Replay operations pending"
            tone="orange"
            icon="⇄"
          />
        </section>

        <section className="cmts-system-policy">
          <span className="cmts-system-policy__icon">◉</span>

          <div>
            <strong>Operational control rule</strong>
            <p>
              Failed runs persist to the failure center. Retry,
              cancel, replay and remediation actions create audit
              and operation-spine events before refreshing the
              affected data.
            </p>
          </div>
        </section>

        <section className="cmts-system-workspace">
          <nav className="cmts-system-tabs">
            <TabButton
              active={activeTab === "health"}
              label="Health Signals"
              count={health.length}
              onClick={() => changeTab("health")}
            />

            <TabButton
              active={activeTab === "runs"}
              label="Operation Runs"
              count={runs.length}
              onClick={() => changeTab("runs")}
            />

            <TabButton
              active={activeTab === "failures"}
              label="Failure Center"
              count={failures.length}
              onClick={() => changeTab("failures")}
            />

            <TabButton
              active={activeTab === "outbox"}
              label="Offline Outbox"
              count={outbox.length}
              onClick={() => changeTab("outbox")}
            />
          </nav>

          <header className="cmts-system-workspace__header">
            <div>
              <h3>{getTabTitle(activeTab)}</h3>
              <p>
                Showing {visibleRows.length} of {activeRows.length}{" "}
                records.
              </p>
            </div>

            <div className="cmts-system-workspace__actions">
              <button
                type="button"
                className="cmts-system-secondary-button"
                onClick={() => {
                  setSearch("");
                  setModuleFilter("ALL");
                  setStatusFilter("ALL");
                }}
              >
                Clear filters
              </button>

              <button
                type="button"
                className="cmts-system-primary-button"
                disabled={visibleRows.length === 0}
                onClick={() => setExportOpen(true)}
              >
                ⇩ Export
              </button>
            </div>
          </header>

          <div className="cmts-system-filters">
            <label className="cmts-system-filter cmts-system-filter--search">
              <span>Search</span>

              <input
                type="search"
                value={search}
                placeholder="Search operation, module, status..."
                onChange={(event) =>
                  setSearch(event.target.value)
                }
              />
            </label>

            <label className="cmts-system-filter">
              <span>Module</span>

              <select
                value={moduleFilter}
                onChange={(event) =>
                  setModuleFilter(event.target.value)
                }
              >
                <option value="ALL">All modules</option>

                {modules.map((moduleName) => (
                  <option key={moduleName} value={moduleName}>
                    {moduleName}
                  </option>
                ))}
              </select>
            </label>

            <label className="cmts-system-filter">
              <span>Status</span>

              <select
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value)
                }
              >
                <option value="ALL">All statuses</option>

                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {humanize(status)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {props.error ? (
            <div className="cmts-system-error">
              <strong>
                Some system operation data could not be loaded
              </strong>
              <span>{props.error}</span>
            </div>
          ) : null}

          <div className="cmts-system-grid">
            <CmtsSmartDataGrid<Row>
              title={`${visibleRows.length} ${getTabTitle(
                activeTab,
              ).toLowerCase()} records`}
              rows={visibleRows}
              columns={activeColumns}
              rowKey="id"
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              sortable
              loading={props.loading}
              actions={gridActions}
              onRowClick={setDetailRow}
              emptyMessage={getEmptyMessage(activeTab)}
            />
          </div>
        </section>
      </PageContainer>

      {actionRow ? (
        <SystemActionsModal
          tab={activeTab}
          row={actionRow}
          busy={Boolean(busyAction)}
          canRetry={Boolean(props.onRetryOperation)}
          canCancel={Boolean(props.onCancelOperation)}
          canReplay={Boolean(props.onReplayOutbox)}
          canReview={Boolean(props.onMarkFailureReviewed)}
          canCreateTask={Boolean(props.onCreateTask)}
          onClose={() => setActionRow(null)}
          onDetail={() => {
            setActionRow(null);
            setDetailRow(actionRow);
          }}
          onCorrelation={() => {
            setActionRow(null);
            setCorrelationRow(actionRow);
          }}
          onRetry={() =>
            void executeAction(
              "retry",
              actionRow,
              props.onRetryOperation,
              "Operation retry submitted",
              `${readOperationType(
                actionRow,
              )} was submitted for retry.`,
            )
          }
          onCancel={() =>
            void executeAction(
              "cancel",
              actionRow,
              props.onCancelOperation,
              "Operation cancelled",
              `${readOperationType(
                actionRow,
              )} was cancelled successfully.`,
            )
          }
          onReplay={() =>
            void executeAction(
              "replay",
              actionRow,
              props.onReplayOutbox,
              "Outbox replay submitted",
              `${readOperationType(
                actionRow,
              )} was submitted for replay.`,
            )
          }
          onReview={() =>
            void executeAction(
              "review",
              actionRow,
              props.onMarkFailureReviewed,
              "Failure marked reviewed",
              "The system operation failure was marked as reviewed.",
            )
          }
          onTask={() =>
            void executeAction(
              "task",
              actionRow,
              props.onCreateTask,
              "Remediation task created",
              `A remediation task was created for ${readOperationType(
                actionRow,
              )}.`,
            )
          }
        />
      ) : null}

      {detailRow ? (
        <SystemDetailModal
          tab={activeTab}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      ) : null}

      {correlationRow ? (
        <CorrelationModal
          rows={correlationRows}
          onClose={() => setCorrelationRow(null)}
          onOpen={(row) => {
            setCorrelationRow(null);
            setDetailRow(row);
          }}
        />
      ) : null}

      {actionResult ? (
        <ActionResultModal
          result={actionResult}
          onClose={() => setActionResult(null)}
        />
      ) : null}

      {exportOpen ? (
        <ExportModal
          tab={activeTab}
          rows={visibleRows}
          format={exportFormat}
          onFormatChange={setExportFormat}
          onClose={() => setExportOpen(false)}
          onExport={() => {
            downloadRows(
              activeTab,
              visibleRows,
              exportFormat,
            );
            setExportOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function SummaryCard(props: {
  readonly label: string;
  readonly value: number;
  readonly description: string;
  readonly tone: string;
  readonly icon: string;
}) {
  return (
    <article
      className={`cmts-system-summary-card cmts-system-summary-card--${props.tone}`}
    >
      <div>
        <span>{props.label}</span>
        <i>{props.icon}</i>
      </div>

      <strong>{props.value}</strong>
      <p>{props.description}</p>
    </article>
  );
}

function TabButton(props: {
  readonly active: boolean;
  readonly label: string;
  readonly count: number;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cmts-system-tab${props.active ? " cmts-system-tab--active" : ""
        }`}
      onClick={props.onClick}
    >
      <span>{props.label}</span>
      <strong>{props.count}</strong>
    </button>
  );
}

function SystemActionsModal(props: {
  readonly tab: TabCode;
  readonly row: Row;
  readonly busy: boolean;
  readonly canRetry: boolean;
  readonly canCancel: boolean;
  readonly canReplay: boolean;
  readonly canReview: boolean;
  readonly canCreateTask: boolean;
  readonly onClose: () => void;
  readonly onDetail: () => void;
  readonly onCorrelation: () => void;
  readonly onRetry: () => void;
  readonly onCancel: () => void;
  readonly onReplay: () => void;
  readonly onReview: () => void;
  readonly onTask: () => void;
}) {
  const canRetryRow =
    props.canRetry &&
    isRetryable(props.row) &&
    !["COMPLETED", "CANCELLED"].includes(
      readRawStatus(props.row),
    );

  const canCancelRow =
    props.canCancel &&
    !["COMPLETED", "CANCELLED", "FAILED"].includes(
      readRawStatus(props.row),
    );

  const canReplayRow =
    props.canReplay &&
    !["COMPLETED", "REPLAYED"].includes(
      readRawReplayStatus(props.row),
    );

  return (
    <ModalShell
      title="System operation actions"
      subtitle={readOperationType(props.row)}
      onClose={props.onClose}
    >
      <div className="cmts-audit-action-list">
        {props.tab === "runs" ? (
          <>
            <ActionOption
              icon="↻"
              title="Retry operation"
              description="Submit this system operation for retry."
              disabled={!canRetryRow || props.busy}
              onClick={props.onRetry}
            />

            <ActionOption
              icon="×"
              title="Cancel operation"
              description="Cancel this active system operation."
              disabled={!canCancelRow || props.busy}
              onClick={props.onCancel}
            />
          </>
        ) : null}

        {props.tab === "failures" ? (
          <>
            <ActionOption
              icon="✓"
              title="Mark reviewed"
              description="Mark this failure as reviewed."
              disabled={!props.canReview || props.busy}
              onClick={props.onReview}
            />

            <ActionOption
              icon="+"
              title="Create remediation task"
              description="Create a task linked to this failure."
              disabled={!props.canCreateTask || props.busy}
              onClick={props.onTask}
            />
          </>
        ) : null}

        {props.tab === "outbox" ? (
          <ActionOption
            icon="⇄"
            title="Replay outbox item"
            description="Replay this pending offline operation."
            disabled={!canReplayRow || props.busy}
            onClick={props.onReplay}
          />
        ) : null}

        <ActionOption
          icon="◉"
          title="View details"
          description="Review readable operation and diagnostic details."
          onClick={props.onDetail}
        />

        {props.tab !== "health" ? (
          <ActionOption
            icon="⛓"
            title="View correlation chain"
            description="Review related runs, failures and outbox events."
            disabled={!readRawCorrelation(props.row)}
            onClick={props.onCorrelation}
          />
        ) : null}
      </div>
    </ModalShell>
  );
}

function ActionOption(props: {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cmts-audit-action-option"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-audit-action-option__icon">
        {props.icon}
      </span>

      <span>
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>

      <span className="cmts-audit-action-option__arrow">
        ›
      </span>
    </button>
  );
}

function SystemDetailModal(props: {
  readonly tab: TabCode;
  readonly row: Row;
  readonly onClose: () => void;
}) {
  const items = getDetailItems(props.tab, props.row);

  return (
    <ModalShell
      title={`${getTabTitle(props.tab)} details`}
      subtitle={
        props.tab === "health"
          ? readCode(props.row)
          : readOperationType(props.row)
      }
      onClose={props.onClose}
      wide
    >
      <div className="cmts-system-detail-banner">
        <div>
          <span>{readModule(props.row)}</span>
          <strong>
            {props.tab === "health"
              ? readSummary(props.row)
              : readOperationType(props.row)}
          </strong>
        </div>

        <span className="cmts-system-detail-status">
          {readStatus(props.row)}
        </span>
      </div>

      <div className="cmts-system-detail-grid">
        {items.map(([label, value]) => (
          <div
            key={label}
            className="cmts-system-detail-item"
          >
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function CorrelationModal(props: {
  readonly rows: readonly Row[];
  readonly onClose: () => void;
  readonly onOpen: (row: Row) => void;
}) {
  return (
    <ModalShell
      title="Correlation chain"
      subtitle={`${props.rows.length} related events`}
      onClose={props.onClose}
      wide
    >
      {props.rows.length ? (
        <div className="cmts-system-correlation-list">
          {props.rows.map((row, index) => (
            <button
              type="button"
              key={String(row.id ?? index)}
              className="cmts-system-correlation-row"
              onClick={() => props.onOpen(row)}
            >
              <span>{index + 1}</span>

              <div>
                <strong>{readOperationType(row)}</strong>
                <small>
                  {readModule(row)} • {readStatus(row)}
                </small>
              </div>

              <em>{readStartedAt(row)}</em>
            </button>
          ))}
        </div>
      ) : (
        <div className="cmts-system-empty">
          No related correlation events were found.
        </div>
      )}

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function ActionResultModal(props: {
  readonly result: ActionResult;
  readonly onClose: () => void;
}) {
  return (
    <ModalShell
      title={props.result.title}
      onClose={props.onClose}
    >
      <div
        className={`cmts-system-action-result ${props.result.success
            ? "cmts-system-action-result--success"
            : "cmts-system-action-result--failure"
          }`}
      >
        <span>{props.result.success ? "✓" : "!"}</span>

        <div>
          <strong>
            {props.result.success
              ? "Operation completed"
              : "Operation failed"}
          </strong>
          <p>{props.result.message}</p>
        </div>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}

function ExportModal(props: {
  readonly tab: TabCode;
  readonly rows: readonly Row[];
  readonly format: ExportFormat;
  readonly onFormatChange: (format: ExportFormat) => void;
  readonly onClose: () => void;
  readonly onExport: () => void;
}) {
  const formats: readonly ExportFormat[] = [
    "CSV",
    "XLS",
    "JSON",
  ];

  return (
    <ModalShell
      title={`Export ${getTabTitle(props.tab)}`}
      subtitle="Download the currently filtered system operation records."
      onClose={props.onClose}
      wide
    >
      <div className="cmts-export-banner">
        <span className="cmts-export-banner__icon">⇩</span>

        <div>
          <small>DATA EXPORT</small>
          <strong>Prepare your export</strong>
          <p>
            Download readable operational values without raw IDs.
          </p>
        </div>

        <span className="cmts-export-record-count">
          {props.rows.length} records
        </span>
      </div>

      <div className="cmts-export-format-grid">
        {formats.map((format) => {
          const selected = format === props.format;

          return (
            <button
              type="button"
              key={format}
              className={`cmts-export-format-card${selected
                  ? " cmts-export-format-card--selected"
                  : ""
                }`}
              onClick={() =>
                props.onFormatChange(format)
              }
            >
              <span className="cmts-export-format-badge">
                {format}
              </span>

              <span
                className={`cmts-export-radio${selected
                    ? " cmts-export-radio--selected"
                    : ""
                  }`}
              >
                {selected ? "✓" : ""}
              </span>

              <strong>
                {format === "CSV"
                  ? "CSV spreadsheet"
                  : format === "XLS"
                    ? "Excel workbook"
                    : "JSON data"}
              </strong>

              <small>
                Export the current filtered operational view.
              </small>
            </button>
          );
        })}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          onClick={props.onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onExport}
        >
          Download {props.format}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly wide?: boolean;
}) {
  return (
    <div
      className="cmts-audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className={`cmts-audit-modal${props.wide ? " cmts-audit-modal--wide" : ""
          }`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="cmts-audit-modal__header">
          <div>
            <h3>{props.title}</h3>
            {props.subtitle ? <p>{props.subtitle}</p> : null}
          </div>

          <button
            type="button"
            className="cmts-audit-modal__close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-audit-modal__body">
          {props.children}
        </div>
      </section>
    </div>
  );
}

function normalizeRows(
  value: unknown,
  prefix: string,
): readonly Row[] {
  return extractRows(value).map((row, index) => ({
    ...row,
    id:
      row.id ??
      row.operationRunId ??
      row.failureId ??
      row.outboxId ??
      row.healthSignalId ??
      row.code ??
      `${prefix}-${index + 1}`,
  }));
}

function extractRows(
  value: unknown,
  depth = 0,
): readonly Row[] {
  if (depth > 5) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }

  if (!isRecord(value)) {
    return [];
  }

  const keys = [
    "items",
    "rows",
    "records",
    "health",
    "healthSignals",
    "runs",
    "operationRuns",
    "failures",
    "outbox",
    "data",
    "result",
    "payload",
  ] as const;

  for (const key of keys) {
    const candidate = value[key];

    if (Array.isArray(candidate)) {
      return candidate.filter(isRecord);
    }

    if (isRecord(candidate)) {
      const nested = extractRows(candidate, depth + 1);

      if (nested.length) {
        return nested;
      }
    }
  }

  return value.id || value.code ? [value] : [];
}

function getDetailItems(
  tab: TabCode,
  row: Row,
): readonly (readonly [string, string])[] {
  if (tab === "health") {
    return [
      ["Signal Code", readCode(row)],
      ["Module", readModule(row)],
      ["Signal Type", readSignalType(row)],
      ["Status", readStatus(row)],
      ["Summary", readSummary(row)],
      ["Updated At", readUpdatedAt(row)],
    ];
  }

  if (tab === "failures") {
    return [
      ["Operation", readOperationType(row)],
      ["Severity", readSeverity(row)],
      ["Status", readStatus(row)],
      ["Failure Reason", readFailureReason(row)],
      ["Retryable", readRetryable(row)],
      ["Correlation", readCorrelation(row)],
    ];
  }

  if (tab === "outbox") {
    return [
      ["Sequence", readSequence(row)],
      ["Entity", readEntity(row)],
      ["Operation", readOperationType(row)],
      ["Status", readStatus(row)],
      ["Replay Status", readReplayStatus(row)],
      ["Failure", readFailureReason(row)],
      ["Correlation", readCorrelation(row)],
    ];
  }

  return [
    ["Started At", readStartedAt(row)],
    ["Operation Type", readOperationType(row)],
    ["Module", readModule(row)],
    ["Status", readStatus(row)],
    ["Result", readResult(row)],
    ["Failure Reason", readFailureReason(row)],
    ["Correlation", readCorrelation(row)],
  ];
}

function readCode(row: Row): string {
  return String(
    row.signalCode ?? row.code ?? "SYSTEM_SIGNAL",
  ).toUpperCase();
}

function readModule(row: Row): string {
  return humanize(
    row.moduleName ?? row.moduleCode ?? row.module,
    "System",
  );
}

function readSignalType(row: Row): string {
  return humanize(
    row.signalName ??
    row.signalType ??
    row.type ??
    row.code,
    "System Health",
  );
}

function readSummary(row: Row): string {
  return String(
    row.summary ??
    row.description ??
    row.message ??
    "System health signal recorded",
  );
}

function readOperationType(row: Row): string {
  return humanize(
    row.operationName ??
    row.operationType ??
    row.operation ??
    row.type,
    "System Operation",
  );
}

function readRawStatus(row: Row): string {
  return String(
    row.statusName ??
    row.status ??
    row.operationStatus ??
    "UNKNOWN",
  )
    .trim()
    .toUpperCase();
}

function readStatus(row: Row): string {
  const status = readRawStatus(row);

  if (
    [
      "HEALTHY",
      "READY",
      "UP",
      "AVAILABLE",
      "COMPLETED",
      "SUCCESS",
      "RESOLVED",
      "REVIEWED",
    ].includes(status)
  ) {
    return `🟢 ${humanize(status)}`;
  }

  if (
    ["RUNNING", "PROCESSING", "QUEUED", "PENDING"].includes(
      status,
    )
  ) {
    return `🔵 ${humanize(status)}`;
  }

  if (
    ["WARNING", "DEGRADED", "RETRYING"].includes(status)
  ) {
    return `🟠 ${humanize(status)}`;
  }

  if (
    ["FAILED", "ERROR", "DOWN", "BLOCKED"].includes(status)
  ) {
    return `🔴 ${humanize(status)}`;
  }

  return `⚪ ${humanize(status)}`;
}

function readResult(row: Row): string {
  return humanize(
    row.resultName ?? row.result ?? row.outcome,
    readRawStatus(row) === "COMPLETED"
      ? "Successful"
      : "Not available",
  );
}

function readFailureReason(row: Row): string {
  const value = String(
    row.failureReason ??
    row.errorMessage ??
    row.reason ??
    row.lastError ??
    "",
  ).trim();

  return value || "No failure recorded";
}

function readSeverity(row: Row): string {
  const severity = String(
    row.severityName ??
    row.severity ??
    row.severityCode ??
    "MEDIUM",
  ).toUpperCase();

  if (severity === "CRITICAL" || severity === "HIGH") {
    return `🔴 ${humanize(severity)}`;
  }

  if (severity === "MEDIUM") {
    return "🟠 Medium";
  }

  return `🔵 ${humanize(severity)}`;
}

function readRetryable(row: Row): string {
  return isRetryable(row)
    ? "🟢 Retry available"
    : "⚪ Not retryable";
}

function isRetryable(row: Row): boolean {
  const value = row.retryable ?? row.isRetryable;

  if (typeof value === "boolean") {
    return value;
  }

  return ["true", "yes", "1"].includes(
    String(value ?? "").toLowerCase(),
  );
}

function readSequence(row: Row): string {
  const value =
    row.sequence ?? row.sequenceNumber ?? row.order;

  return value === undefined || value === null
    ? "Not assigned"
    : String(value);
}

function readEntity(row: Row): string {
  return humanize(
    row.entityName ?? row.entityType ?? row.aggregateType,
    "System Record",
  );
}

function readRawReplayStatus(row: Row): string {
  return String(
    row.replayStatus ??
    row.replayState ??
    row.status ??
    "PENDING",
  )
    .trim()
    .toUpperCase();
}

function readReplayStatus(row: Row): string {
  const status = readRawReplayStatus(row);

  if (
    ["COMPLETED", "REPLAYED", "PROCESSED"].includes(status)
  ) {
    return `🟢 ${humanize(status)}`;
  }

  if (["FAILED", "BLOCKED"].includes(status)) {
    return `🔴 ${humanize(status)}`;
  }

  return `🟠 ${humanize(status)}`;
}

function readStartedAt(row: Row): string {
  return formatDateTime(
    row.startedAt ??
    row.createdAt ??
    row.queuedAt ??
    row.occurredAt,
  );
}

function readUpdatedAt(row: Row): string {
  return formatDateTime(
    row.updatedAt ??
    row.occurredAt ??
    row.createdAt ??
    row.detectedAt,
  );
}

function readRawCorrelation(row: Row | null): string {
  if (!row) {
    return "";
  }

  return String(
    row.correlationId ??
    row.requestId ??
    row.traceId ??
    "",
  ).trim();
}

function readCorrelation(row: Row): string {
  return readRawCorrelation(row)
    ? "🔗 Chain available"
    : "No correlation";
}

function getTabTitle(tab: TabCode): string {
  return tab === "health"
    ? "System Health Signals"
    : tab === "runs"
      ? "Operation Runs"
      : tab === "failures"
        ? "Failure Center"
        : "Offline Outbox and Replay";
}

function getEmptyMessage(tab: TabCode): string {
  return tab === "health"
    ? "No system health signals were returned."
    : tab === "runs"
      ? "No system operation runs were returned."
      : tab === "failures"
        ? "No system operation failures were returned."
        : "No offline outbox records were returned.";
}

function uniqueValues(
  values: readonly string[],
): readonly string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function humanize(
  value: unknown,
  fallback = "Not available",
): string {
  const text = String(value ?? "").trim();

  if (!text || isUuid(text)) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function formatDateTime(value: unknown): string {
  const text = String(value ?? "").trim();

  if (!text) {
    return "Not recorded";
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return humanize(text, "Not recorded");
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isRecord(value: unknown): value is Row {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The system operation could not be completed.";
}

function downloadRows(
  tab: TabCode,
  rows: readonly Row[],
  format: ExportFormat,
) {
  const exportRows = rows.map((row) => {
    if (tab === "health") {
      return {
        Code: readCode(row),
        Module: readModule(row),
        Signal: readSignalType(row),
        Status: readStatus(row).replace(/^[^\s]+\s/, ""),
        Summary: readSummary(row),
        Updated: readUpdatedAt(row),
      };
    }

    if (tab === "failures") {
      return {
        Operation: readOperationType(row),
        Severity: readSeverity(row).replace(
          /^[^\s]+\s/,
          "",
        ),
        Status: readStatus(row).replace(/^[^\s]+\s/, ""),
        Reason: readFailureReason(row),
        Retryable: readRetryable(row).replace(
          /^[^\s]+\s/,
          "",
        ),
        Correlation: readCorrelation(row).replace(
          /^[^\s]+\s/,
          "",
        ),
      };
    }

    if (tab === "outbox") {
      return {
        Sequence: readSequence(row),
        Entity: readEntity(row),
        Operation: readOperationType(row),
        Status: readStatus(row).replace(/^[^\s]+\s/, ""),
        Replay: readReplayStatus(row).replace(
          /^[^\s]+\s/,
          "",
        ),
        Failure: readFailureReason(row),
        Correlation: readCorrelation(row).replace(
          /^[^\s]+\s/,
          "",
        ),
      };
    }

    return {
      Started: readStartedAt(row),
      "Operation Type": readOperationType(row),
      Module: readModule(row),
      Status: readStatus(row).replace(/^[^\s]+\s/, ""),
      Result: readResult(row),
      "Failure Reason": readFailureReason(row),
      Correlation: readCorrelation(row).replace(
        /^[^\s]+\s/,
        "",
      ),
    };
  });

  exportRecords(
    exportRows,
    format,
    `system-${tab}`,
  );
}

function exportRecords(
  rows: readonly Row[],
  format: ExportFormat,
  prefix: string,
) {
  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, "-");
  const headers = Object.keys(rows[0] ?? {});

  if (format === "JSON") {
    saveBlob(
      JSON.stringify(rows, null, 2),
      `${prefix}-${timestamp}.json`,
      "application/json;charset=utf-8",
    );
    return;
  }

  if (format === "XLS") {
    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headers
      .map((header) => `<th>${escapeHtml(header)}</th>`)
      .join("")}</tr></thead><tbody>${rows
        .map(
          (row) =>
            `<tr>${headers
              .map(
                (header) =>
                  `<td>${escapeHtml(
                    String(row[header] ?? ""),
                  )}</td>`,
              )
              .join("")}</tr>`,
        )
        .join("")}</tbody></table></body></html>`;

    saveBlob(
      html,
      `${prefix}-${timestamp}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    return;
  }

  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) =>
      headers
        .map((header) => csvCell(row[header]))
        .join(","),
    ),
  ].join("\n");

  saveBlob(
    `\uFEFF${csv}`,
    `${prefix}-${timestamp}.csv`,
    "text/csv;charset=utf-8",
  );
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function saveBlob(
  content: string,
  fileName: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}